import React from 'react';
import { Box, Container, Paper, Typography, Link, Button } from '@mui/material';

const WebinarPage: React.FC = () => {
  return (
    <Container maxWidth="sm" sx={{ my: 4 }}>
      <Paper elevation={1} sx={{ overflow: 'hidden', border: 'none' }}>
        {/* Header image */}
        <Box sx={{ width: '100%' }}>
          <Link
            href="https://forms.gle/11v85S28BGAnb66U8"
            target="_blank"
            rel="noopener"
            underline="none"
            aria-label="Register for the webinar (opens in a new tab)"
            sx={{ display: 'block', lineHeight: 0 }}
          >
            <Box
              component="img"
              src="https://raw.githubusercontent.com/Accounting-AI/public-assets/refs/heads/main/valuechains_invite/version_1/webinar-poster.png"
              alt="Webinar – Productive Capabilities Data Lab"
              sx={{ display: 'block', width: '100%', height: 'auto', cursor: 'pointer' }}
            />
          </Link>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          
          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            <Link href="https://rubidiumdata.com/" target="_blank" rel="noopener" underline="hover" color="success.main">
              RubidiumData
            </Link>{' '}
            and{' '}
            <Link href="https://www.i3-t.org/" target="_blank" rel="noopener" underline="hover" color="success.main">
              i3T
            </Link>{' '}
            invite you to a virtual briefing and demo of the Productive Capabilities Data Lab—a cutting-edge
            initiative developing innovative data analytics to guide industrial strategies and help policymakers move
            from analysis to implementation.
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            <strong>Date:</strong> Wednesday, December 3, 2025
            <br />
            <strong>Time:</strong> 13:00 EST
            <br />
            <strong>Format:</strong> Virtual
            <br />
            <strong>Registration:</strong>{' '}
            <Link
              href="https://forms.gle/11v85S28BGAnb66U8"
              target="_blank"
              rel="noopener"
              underline="hover"
              color="success.main"
            >
              https://forms.gle/11v85S28BGAnb66U8
            </Link>
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            Following our soft launch at New York Climate Week and Africa Industrialization Week, this briefing will
            demonstrate how our tools decompose value chains, map components to trade codes, and identify concrete,
            granular productive capabilities at the firm level and beyond to better understand current, latent, and
            potential opportunities for industrial development. We will highlight how decision-makers can use these
            tools to identify strategic sectors, diagnose bottlenecks, and design actionable policy and investment
            strategies.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 700, color: 'text.primary' }}>
            Agenda:
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            <li>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Introduction by Isabel Estevez (
                <Link href="https://www.i3-t.org/" target="_blank" rel="noopener" underline="hover" color="success.main">
                  i3T
                </Link>
                )
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Demo and presentation by Justus Schollmeyer (
                <Link
                  href="https://rubidiumdata.com/"
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  color="success.main"
                >
                  RubidiumData
                </Link>
                )
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Comments on policy applications by Andrés Arauz (
                <Link href="https://www.i3-t.org/" target="_blank" rel="noopener" underline="hover" color="success.main">
                  i3T
                </Link>
                /{' '}
                <Link
                  href="https://cepr.net/people/andres-arauz/"
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  color="success.main"
                >
                  CEPR
                </Link>
                ) and Jonah Allen (
                <Link
                  href="https://jainfamilyinstitute.org/our-team/jonah-allen/"
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                  color="success.main"
                >
                  Jain Family Institute
                </Link>
                )
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Q&amp;A
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Closing remarks by Isabel Estevez (
                <Link href="https://www.i3-t.org/" target="_blank" rel="noopener" underline="hover" color="success.main">
                  i3T
                </Link>
                )
              </Typography>
            </li>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 700, color: 'text.primary' }}>
            Speakers:
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            <strong>Isabel Estevez</strong> – Co-Executive Director at{' '}
            <Link href="https://www.i3-t.org/" target="_blank" rel="noopener" underline="hover" color="success.main">
              i3T
            </Link>{' '}
            and Ph.D. from the University of Cambridge, specializing in economic transformations and green industrial
            policy, with experience advising governments, multilateral organizations, and civil society across the US,
            Europe, and Latin America.
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            <strong>Justus Schollmeyer</strong> – Co-Founder at{' '}
            <Link href="https://rubidiumdata.com/" target="_blank" rel="noopener" underline="hover" color="success.main">
              RubidiumData
            </Link>{' '}
            and Ph.D. in Economics from the University of Bremen, specializing in AI-driven automation of value-chain
            analysis and qualitative data processing.
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            <strong>Andrés Arauz</strong> – Senior Research Fellow at{' '}
            <Link
              href="https://cepr.net/people/andres-arauz/"
              target="_blank"
              rel="noopener"
              underline="hover"
              color="success.main"
            >
              CEPR
            </Link>
            . Ph.D. in economics from UNAM with extensive experience leading industrial policies and strategic public
            investment. Former: Minister of Knowledge, General Director of Procurement, and head of the Committee of
            the National Standards Agency of Ecuador.
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
            <strong>Jonah Allen</strong> – Lead Researcher for Energy Transition Value Chains at the{' '}
            <Link
              href="https://jainfamilyinstitute.org/our-team/jonah-allen/"
              target="_blank"
              rel="noopener"
              underline="hover"
              color="success.main"
            >
              Jain Family Institute
            </Link>{' '}
            with a Ph.D. in Mineral and Energy Economics, focusing on equitable benefit-sharing in critical resource
            extraction and trade for the energy transition.
          </Typography>

          <Typography variant="body2" sx={{ my: 2, color: 'text.primary' }}>
            For more detail about the DataLab&apos;s approach to industrial strategy and productive capabilities
            mapping, please visit us at:{' '}
            <Link href="https://valuechains.ai/" target="_blank" rel="noopener" underline="hover" color="success.main">
              https://valuechains.ai/
            </Link>
          </Typography>

          {/* CTA */}
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <Button
              component="a"
              href="https://forms.gle/11v85S28BGAnb66U8"
              target="_blank"
              rel="noopener"
              variant="contained"
              color="success"
              sx={{ fontWeight: 700, px: 3.5, py: 1.75 }}
            >
              Register for the webinar
            </Button>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.primary' }}>
              Register here:{' '}
              <Link
                href="https://forms.gle/11v85S28BGAnb66U8"
                target="_blank"
                rel="noopener"
                underline="hover"
                color="success.main"
              >
                https://forms.gle/11v85S28BGAnb66U8
              </Link>
            </Typography>
          </Box>

         
          {/* Speakers image */}
          <Box sx={{ width: '100%', mt: 1 }}>
            <Box
              component="img"
              src="https://raw.githubusercontent.com/Accounting-AI/public-assets/refs/heads/main/valuechains_invite/version_1/speakers-poster.png"
              alt="Webinar Speakers"
              sx={{ display: 'block', width: '100%', height: 'auto', borderRadius: 1 }}
            />
          </Box>

          
        </Box>

        {/* Logos footer */}
        <Box
          sx={{
            p: 2.5,
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(45deg,#4eac53,#30aaec)',
          }}
        >
          <Box
            component="img"
            src="https://raw.githubusercontent.com/Accounting-AI/public-assets/refs/heads/main/valuechains_invite/version_1/i3t-logo.png"
            alt="i3T"
            sx={{ display: 'inline-block', maxWidth: 150, height: 'auto', m: 1.5 }}
          />
          <Box
            component="img"
            src="https://raw.githubusercontent.com/Accounting-AI/public-assets/refs/heads/main/valuechains_invite/version_1/rubidiumdata-logo.png"
            alt="RubidiumData"
            sx={{ display: 'inline-block', maxWidth: 200, height: 'auto', m: 1.5 }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default WebinarPage;


